// ─────────────────────────────────────────────
// services/fuelService.js
// Fuel price fetching and dump run management
// Uses: EIA API (free, no key required for demo)
// Uses: Supabase (MIT client)
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import { supabase } from "./supabaseService";

/**
 * Fetch current Rocky Mountain fuel price from EIA API.
 * EIA (U.S. Energy Information Administration) — public domain data, free to use commercially.
 * API docs: https://www.eia.gov/opendata/
 */
export async function fetchEIAFuelPrice() {
  const apiKey = process.env.REACT_APP_EIA_API_KEY;
  if (!apiKey) { console.warn("EIA API key not configured"); return null; }
  try {
    const res = await fetch(
      `https://api.eia.gov/v2/petroleum/pri/gnd/data/?api_key=${encodeURIComponent(apiKey)}&frequency=weekly&data[0]=value&facets[series][]=EMM_EPM0_PTE_R40_DPG&sort[0][column]=period&sort[0][direction]=desc&length=1`
    );
    const json = await res.json();
    const price = json?.response?.data?.[0]?.value;
    if (price && !isNaN(parseFloat(price))) return parseFloat(price);
  } catch (e) {
    console.warn("EIA fuel price fetch failed:", e);
  }
  return null;
}

/** Load fuel settings from Supabase */
export async function loadFuelSettings() {
  const { data } = await supabase
    .from("fuel_settings")
    .select("*")
    .order("last_updated", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data || null;
}

/** Save / upsert fuel settings */
export async function saveFuelSettings(existing, updates) {
  const payload = { ...existing, ...updates, last_updated: new Date().toISOString() };
  const { data: row } = await supabase.from("fuel_settings").select("id").limit(1).maybeSingle();
  if (row?.id) {
    await supabase.from("fuel_settings").update(payload).eq("id", row.id);
  } else {
    await supabase.from("fuel_settings").insert([payload]);
  }
  return payload;
}

/** Load recent dump runs */
export async function loadDumpRuns(limit = 20) {
  const { data } = await supabase
    .from("dump_runs")
    .select("*")
    .order("timestamp", { ascending: false })
    .limit(limit);
  return data || [];
}

/** Log a new dump run */
export async function logDumpRun({ capacityBefore, dumpFee, bags, notes, adminId }) {
  await supabase.from("dump_runs").insert([{
    capacity_before: capacityBefore,
    dump_fee: parseFloat(dumpFee) || 0,
    bags_dumped: parseInt(bags) || null,
    driver_notes: notes?.trim() || null,
    admin_id: adminId,
  }]);
}
